import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Save, Lock } from 'lucide-react';
import { API_BASE_URL } from '../../../config';
import './DatosPersonales.css';

export default function DatosPersonales({ onUserNameChange, onProfileUpdate, initialProfile }) {
  const hasInitialProfile = Boolean(
    initialProfile && (initialProfile.name || initialProfile.email || initialProfile.phone),
  );
  const [loading, setLoading] = useState(!hasInitialProfile);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (hasInitialProfile) {
      setForm({
        name: initialProfile.name || '',
        email: initialProfile.email || '',
        phone: initialProfile.phone || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setLoading(false);
      return;
    }
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!initialProfile) return;
    setForm((prev) => ({
      ...prev,
      name: initialProfile.name || prev.name,
      email: initialProfile.email || prev.email,
      phone: initialProfile.phone || prev.phone,
    }));
    setLoading(false);
  }, [initialProfile]);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setForm({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        if (typeof onUserNameChange === 'function') {
          onUserNameChange(data.name || 'Cliente');
        }
      }
    } catch {
      setMessage({ type: 'error', text: 'No se pudo cargar tu perfil.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setMessage(null);
  };

  const parseApiDetail = (detail) => {
    if (!detail) return 'Error al guardar';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length) {
      return detail.map((d) => d.msg || JSON.stringify(d)).join(' ');
    }
    return 'Error al guardar';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const wantsPasswordChange = Boolean(form.newPassword.trim() || form.confirmPassword.trim());
    if (wantsPasswordChange) {
      if (!form.currentPassword) {
        setMessage({ type: 'error', text: 'Escribe tu contraseña actual para cambiarla.' });
        setSaving(false);
        return;
      }
      if (form.newPassword.length < 6) {
        setMessage({ type: 'error', text: 'La nueva contraseña debe tener al menos 6 caracteres.' });
        setSaving(false);
        return;
      }
      if (form.newPassword !== form.confirmPassword) {
        setMessage({ type: 'error', text: 'La nueva contraseña y la confirmación no coinciden.' });
        setSaving(false);
        return;
      }
    }
    try {
      const token = localStorage.getItem('token');
      const body = {
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim()
      };
      if (wantsPasswordChange) {
        body.current_password = form.currentPassword;
        body.new_password = form.newPassword;
      }
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(parseApiDetail(err.detail));
      }
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
      }
      setMessage({ type: 'success', text: 'Datos actualizados correctamente.' });
      setForm((prev) => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      if (typeof onUserNameChange === 'function') {
        onUserNameChange(data.name || 'Cliente');
      }
      if (typeof onProfileUpdate === 'function' && data) {
        onProfileUpdate({
          nombre: data.name || '',
          email: data.email || '',
          telefono: data.phone || '',
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Error al actualizar.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="datos-personales datos-personales-loading">
        <div className="datos-personales-spinner" />
        <p>Cargando datos...</p>
      </div>
    );
  }

  return (
    <div className="datos-personales">
      <h1 className="datos-personales-title">Datos personales</h1>
      <p className="datos-personales-subtitle">Revisa y actualiza tu información de contacto</p>

      <form className="datos-personales-form" onSubmit={handleSubmit}>
        {message && (
          <div className={`datos-personales-message datos-personales-message-${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="datos-personales-field">
          <label htmlFor="dp-name">
            <User size={18} />
            Nombre completo
          </label>
          <input
            id="dp-name"
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Tu nombre"
            autoComplete="name"
          />
        </div>

        <div className="datos-personales-field">
          <label htmlFor="dp-email">
            <Mail size={18} />
            Correo electrónico
          </label>
          <input
            id="dp-email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="correo@ejemplo.com"
            autoComplete="email"
          />
        </div>

        <div className="datos-personales-field">
          <label htmlFor="dp-phone">
            <Phone size={18} />
            Teléfono
          </label>
          <input
            id="dp-phone"
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="300 123 4567"
            autoComplete="tel"
          />
        </div>

        <div className="datos-personales-divider" />
        <h2 className="datos-personales-section-title">Cambiar contraseña</h2>
        <p className="datos-personales-section-hint">
          Déjalo en blanco si no quieres cambiarla. Si usaste una contraseña temporal, cámbiala aquí.
        </p>

        <div className="datos-personales-field">
          <label htmlFor="dp-current-pw">
            <Lock size={18} />
            Contraseña actual
          </label>
          <input
            id="dp-current-pw"
            type="password"
            name="currentPassword"
            value={form.currentPassword}
            onChange={handleChange}
            placeholder="Solo si cambias la contraseña"
            autoComplete="current-password"
          />
        </div>

        <div className="datos-personales-field">
          <label htmlFor="dp-new-pw">
            <Lock size={18} />
            Nueva contraseña
          </label>
          <input
            id="dp-new-pw"
            type="password"
            name="newPassword"
            value={form.newPassword}
            onChange={handleChange}
            placeholder="Mínimo 6 caracteres"
            autoComplete="new-password"
          />
        </div>

        <div className="datos-personales-field">
          <label htmlFor="dp-confirm-pw">
            <Lock size={18} />
            Confirmar nueva contraseña
          </label>
          <input
            id="dp-confirm-pw"
            type="password"
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
            placeholder="Repite la nueva contraseña"
            autoComplete="new-password"
          />
        </div>

        <button
          type="submit"
          className="datos-personales-submit"
          disabled={saving}
        >
          <Save size={20} />
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </form>
    </div>
  );
}
